/**
 * SVG Destiny Matrix Octagon visualization.
 */

import { useMemo } from 'react';

interface DestinyMatrixData {
  positions: Record<string, number>;
  lines: Record<string, number[]>;
  center: number;
  family_center: number;
}

interface MatrixOctagonProps {
  matrix: DestinyMatrixData;
}

const SIZE = 360;
const CENTER_X = SIZE / 2;
const CENTER_Y = SIZE / 2;
const RADIUS = 140;
const NODE_R = 18;

const LINE_COLORS: Record<string, string> = {
  personal_purpose: '#B478FF',
  social_purpose: '#64B4FF',
  money: '#50DC78',
  relationships: '#FF82AA',
  health: '#FFC850',
  talents: '#FF8C3C',
};

const OCTAGON_ANGLES: Record<string, number> = {
  B: 0, G: 45, C: 90, H: 135,
  D: 180, I: 225, A: 270, F: 315,
};

function angleToXY(angleDeg: number, r: number): [number, number] {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return [CENTER_X + r * Math.cos(rad), CENTER_Y + r * Math.sin(rad)];
}

function midpoint(p1: [number, number], p2: [number, number]): [number, number] {
  return [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
}

export function MatrixOctagon({ matrix }: MatrixOctagonProps) {
  const coords = useMemo(() => {
    const c: Record<string, [number, number]> = {};

    // Centers (offset vertically)
    c['E'] = [CENTER_X, CENTER_Y - 30];
    c['L2'] = [CENTER_X, CENTER_Y + 30];

    // Octagon vertices
    for (const [key, angle] of Object.entries(OCTAGON_ANGLES)) {
      c[key] = angleToXY(angle, RADIUS);
    }

    // Side midpoints
    c['J'] = midpoint(c['A'], c['E']);
    c['K'] = midpoint(c['B'], c['E']);
    c['L'] = midpoint(c['C'], c['E']);
    c['M'] = midpoint(c['D'], c['E']);

    // Family extensions
    c['F2'] = midpoint(c['F'], c['L2']);
    c['G2'] = midpoint(c['G'], c['L2']);
    c['H2'] = midpoint(c['H'], c['L2']);
    c['I2'] = midpoint(c['I'], c['L2']);

    return c;
  }, []);

  const pos = matrix.positions;

  // Octagon edges
  const octKeys = ['B', 'G', 'C', 'H', 'D', 'I', 'A', 'F'];
  const octLines = octKeys.map((key, i) => {
    const next = octKeys[(i + 1) % octKeys.length];
    return { from: coords[key], to: coords[next] };
  });

  // Diagonals
  const diagonals = [
    { from: coords['A'], to: coords['C'] },
    { from: coords['B'], to: coords['D'] },
  ];

  // Family square
  const famKeys = ['F', 'G', 'H', 'I'];
  const famLines = famKeys.map((key, i) => {
    const next = famKeys[(i + 1) % famKeys.length];
    return { from: coords[key], to: coords[next] };
  });

  // Colored lines
  const linePaths: Record<string, string[]> = {
    personal_purpose: ['A', 'E', 'C'],
    social_purpose: ['B', 'E', 'D'],
    money: ['F', 'F2', 'L2'],
    relationships: ['G', 'G2', 'L2'],
    health: ['I', 'I2', 'L2'],
    talents: ['H', 'H2', 'L2'],
  };

  const allNodes = Object.entries(coords);

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} width="100%" style={{ maxWidth: SIZE }}>
      {/* Background */}
      <rect width={SIZE} height={SIZE} fill="#140F28" rx="12" />

      {/* Octagon edges */}
      {octLines.map((line, i) => (
        <line key={`oct-${i}`} x1={line.from[0]} y1={line.from[1]} x2={line.to[0]} y2={line.to[1]}
          stroke="#5040A0" strokeWidth="1" opacity="0.5" />
      ))}

      {/* Diagonals */}
      {diagonals.map((line, i) => (
        <line key={`diag-${i}`} x1={line.from[0]} y1={line.from[1]} x2={line.to[0]} y2={line.to[1]}
          stroke="#5040A0" strokeWidth="1" opacity="0.4" />
      ))}

      {/* Family square */}
      {famLines.map((line, i) => (
        <line key={`fam-${i}`} x1={line.from[0]} y1={line.from[1]} x2={line.to[0]} y2={line.to[1]}
          stroke="#7864B4" strokeWidth="1" opacity="0.3" />
      ))}

      {/* Colored line connections */}
      {Object.entries(linePaths).map(([lineKey, path]) => {
        const color = LINE_COLORS[lineKey] || '#CCC';
        return path.slice(0, -1).map((key, i) => {
          const from = coords[key];
          const to = coords[path[i + 1]];
          return (
            <line key={`${lineKey}-${i}`} x1={from[0]} y1={from[1]} x2={to[0]} y2={to[1]}
              stroke={color} strokeWidth="2.5" opacity="0.7" />
          );
        });
      })}

      {/* Nodes */}
      {allNodes.map(([key, [x, y]]) => {
        const isCenter = key === 'E' || key === 'L2';
        const r = isCenter ? NODE_R + 3 : NODE_R;
        const fill = isCenter ? '#8C50C8' : '#3C2864';
        const value = pos[key] ?? 0;
        return (
          <g key={key}>
            <circle cx={x} cy={y} r={r} fill={fill} stroke="#FFF" strokeWidth="1.5" />
            <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="central"
              fill="#FFF" fontSize="13" fontWeight="bold">
              {value}
            </text>
            <text x={x} y={y + r + 12} textAnchor="middle"
              fill="#B8B8D0" fontSize="9">
              {key}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
